import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Availability indicator rendered above the buy buttons.
 *
 * The buy-buttons markup is not re-rendered on variant change, so this mirrors
 * the Liquid logic in 'product-availability': it combines the purchasability of
 * the selected variant with the product-level `custom.availability` metafield
 * (exposed as `data-availability-pref`) and swaps the label accordingly.
 *
 * @typedef {object} AvailabilityIndicatorRefs
 * @property {HTMLElement} label
 *
 * @extends {Component<AvailabilityIndicatorRefs>}
 */
class AvailabilityIndicator extends Component {
  requiredRefs = ['label'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    this.closest('.shopify-section, dialog, product-card')?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdate,
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    this.#apply(event.detail.resource);
  };

  /**
   * @param {object | undefined | null} variant
   */
  #apply(variant) {
    const state = this.#state(variant);
    const labels = {
      en_stock: this.dataset.labelEnStock,
      solo_online: this.dataset.labelSoloOnline,
      bajo_pedido: this.dataset.labelBajoPedido,
    };

    this.hidden = state === 'agotado';
    this.classList.toggle('availability-indicator--solo-online', state === 'solo_online');
    this.classList.toggle('availability-indicator--bajo-pedido', state === 'bajo_pedido');

    const label = labels[state];
    if (label) this.refs.label.textContent = label;
  }

  /**
   * Mirrors the 'product-availability' snippet.
   *
   * @param {object | undefined | null} variant
   * @returns {'en_stock' | 'solo_online' | 'agotado' | 'bajo_pedido'}
   */
  #state(variant) {
    const pref = this.dataset.availabilityPref;

    let purchasable = Boolean(variant?.available);
    if (
      purchasable &&
      variant.inventory_management === 'shopify' &&
      variant.inventory_policy === 'deny' &&
      (variant.inventory_quantity ?? 0) <= 0
    ) {
      purchasable = false;
    }

    if (purchasable) return pref === 'solo_online' ? 'solo_online' : 'en_stock';
    return pref === 'bajo_pedido' ? 'bajo_pedido' : 'agotado';
  }
}

if (!customElements.get('availability-indicator')) {
  customElements.define('availability-indicator', AvailabilityIndicator);
}
